import { formatDate } from "~/utils/datetime"

export const PO_GRANULARITIES = ["vendor", "product", "variant"] as const

export type PoGranularity = (typeof PO_GRANULARITIES)[number]

export const DEFAULT_PO_GRANULARITY: PoGranularity = "vendor"

export const PO_GRANULARITY_LABELS: Record<PoGranularity, string> = {
  vendor: "Vendor",
  product: "Product",
  variant: "Variant",
}

export const PO_GRANULARITY_DESCRIPTIONS: Record<PoGranularity, string> = {
  vendor: "One purchase order per vendor.",
  product:
    "One purchase order per product. A product belongs to one vendor, so this splits each vendor's order by product.",
  variant:
    "One purchase order per variant. A variant belongs to one product, so this splits each product's order by variant.",
}

export function asPoGranularity(value: string | null | undefined): PoGranularity {
  return PO_GRANULARITIES.includes(value as PoGranularity)
    ? (value as PoGranularity)
    : DEFAULT_PO_GRANULARITY
}

/**
 * Identity of the product/variant a SKU belongs to, plus its resolved vendor.
 * `productId`/`variantId` are what POs are keyed on; the `*Name` fields are only
 * used to build a human-readable PO name.
 *
 * Deliberately carries no vendor *name*: nothing here reads one, and a required-but-unread
 * field only tempts callers who just want a key into filling it with the vendor id or "".
 * Pass the display name straight to {@link buildPurchaseOrderName} instead.
 */
export type PoGroupMeta = {
  vendorId: string
  productId?: string | null
  productName?: string | null
  variantId?: string | null
  variantName?: string | null
}

/**
 * Grouping key for one purchase order.
 *
 * Vendor is always part of the key, even at product/variant granularity: vendor is resolved
 * per variant (preferredVendorId with a vendors[0] fallback, plus vendorManufactorName on the
 * fashion path), so two variants of the same product can resolve to different vendors — and
 * `PurchaseOrder.vendorId` is a single non-null column. Keying on product alone would silently
 * pick one of them.
 *
 * Falls back to the next coarser level when the finer id is missing, so a SKU with no productId
 * lands in its vendor's order rather than an orphan group.
 */
export function poGroupKey(granularity: PoGranularity, meta: PoGroupMeta): string {
  if (granularity === "variant" && meta.variantId) return `${meta.vendorId}__v__${meta.variantId}`
  if (granularity !== "vendor" && meta.productId) return `${meta.vendorId}__p__${meta.productId}`
  return meta.vendorId
}

/**
 * Extra segment for the PO name, so orders split by product/variant don't all share
 * `${vendor} - ${date}`. Undefined at vendor granularity — names stay as they are today.
 */
export function poGroupNameDiscriminator(
  granularity: PoGranularity,
  meta: PoGroupMeta,
): string | undefined {
  if (granularity === "variant" && meta.variantId) {
    return meta.variantName || meta.productName || meta.variantId
  }
  if (granularity !== "vendor" && meta.productId) {
    return meta.productName || meta.productId
  }
  return undefined
}

/** Without a discriminator this is byte-identical to the pre-granularity name. */
export function buildPurchaseOrderName(
  vendorName: string,
  expectedArrivalDate: Date,
  nameDiscriminator?: string | null,
): string {
  const date = formatDate(expectedArrivalDate)
  return nameDiscriminator
    ? `${vendorName} - ${nameDiscriminator} - ${date}`
    : `${vendorName} - ${date}`
}
